import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LOCALES } from '../../i18n/config';

// Exported so JsonLd's Organization/WebSite/BlogPosting schemas
// (Layout.jsx, BlogPost.jsx) share the same domain/name instead of a
// second hardcoded copy.
export const SITE = 'https://openconceptlab.org';
export const SITE_NAME = 'Open Concept Lab';
const DEFAULT_DESCRIPTION = 'Open Concept Lab — smart terminology tools for more efficient and equitable healthcare';

// Fallback share card for every page that has no image of its own — without
// it LinkedIn/Slack/X unfurl a bare grey box (docs/SEO-AUDIT.md §2.2 — 13
// static URLs + 12 posts). 1200×630, the ratio all three expect. Interim
// artwork built from the OCL mark; source is docs/og-default.svg, so a
// designer's replacement drops in at this same path with no code change.
// Pass `image={null}` to opt a page out entirely.
export const DEFAULT_OG_IMAGE = '/images/og-default.png';

// Per-page title/description/OG/canonical + hreflang alternates. Language
// is client-side (same URL per locale), so alternates point at the
// canonical URL for every supported locale.
const PageMeta = ({ title, description = DEFAULT_DESCRIPTION, image = DEFAULT_OG_IMAGE, type = 'website', robots }) => {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const normalizedPath = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const canonical = `${SITE}${normalizedPath}`;
  const fullTitle = title ? `${title} — ${SITE_NAME}` : SITE_NAME;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      {image && <meta property="og:image" content={image.startsWith('http') ? image : `${SITE}${image}`} />}
      {image && <meta property="og:image:alt" content={title || SITE_NAME} />}
      {/* Only asserted for the default card, which really is 1200×630. Most
          per-post images are not (§2.3 measured 1409×525, 960×1440, …), and
          declaring dimensions an image doesn't have is worse than omitting
          them — see SEO-AUDIT §14.4 #13. Flat siblings, not a Fragment:
          react-helmet-async silently drops Fragment-wrapped tags. */}
      {image === DEFAULT_OG_IMAGE && <meta property="og:image:width" content="1200" />}
      {image === DEFAULT_OG_IMAGE && <meta property="og:image:height" content="630" />}
      <meta name="twitter:card" content={image ? 'summary_large_image' : 'summary'} />
      <meta property="og:locale" content={i18n.resolvedLanguage || 'en'} />
      {robots && <meta name="robots" content={robots} />}
      {LOCALES.map(locale => (
        <link key={locale.code} rel="alternate" hrefLang={locale.code} href={canonical} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={canonical} />
    </Helmet>
  );
};

export default PageMeta;
